<template>
  <div class="user-info-stats">
    <div class="page-header">
      <h2>用户信息统计</h2>
      <div class="header-controls">
        <div class="date-search">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :shortcuts="dateShortcuts"
          />
          <el-button type="primary" :icon="Search" @click="handleSearch">搜索</el-button>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>总用户数</span>
                <el-icon><User /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.totalUsers }}</div>
              <div class="stat-label">注册用户总数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>设备信息上传数</span>
                <el-icon><Monitor /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.totalDevices }}</div>
              <div class="stat-label">已上传设备信息的用户数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>上传率</span>
                <el-icon><DataLine /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.uploadRate }}%</div>
              <div class="stat-label">设备信息上传比例</div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 用户信息统计图表 -->
    <div class="chart-container">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card class="chart-card">
            <template #header>
              <div class="card-header">
                <span>单位类型分布</span>
              </div>
            </template>
            <div class="chart-content">
              <ChartBox
                :chart-data="institutionChartData"
                :chart-options="pieChartOptions"
                type="pie"
                height="400px"
              />
            </div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card class="chart-card">
            <template #header>
              <div class="card-header">
                <span>学历分布</span>
              </div>
            </template>
            <div class="chart-content">
              <ChartBox
                :chart-data="professionChartData"
                :chart-options="pieChartOptions"
                type="pie"
                height="400px"
              />
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 设备信息列表 -->
    <div class="device-list">
      <el-card>
        <template #header>
          <div class="card-header">
            <span>设备信息详情</span>
            <div class="search-controls">
              <el-input
                v-model="searchQuery"
                placeholder="搜索设备信息"
                class="search-input"
                clearable
                @clear="handleSearchClear"
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
              <el-select
                v-model="searchType"
                placeholder="搜索类型"
                class="search-type"
              >
                <el-option label="用户名" value="username" />
                <el-option label="操作系统" value="os" />
                <el-option label="CPU" value="cpu" />
                <el-option label="内存" value="ram" />
              </el-select>
            </div>
          </div>
        </template>
        <el-table :data="filteredDeviceList" style="width: 100%">
          <el-table-column prop="user_name" label="用户名" />
          <el-table-column label="操作系统">
            <template #default="{ row }">
              {{ JSON.parse(row.computer_info).os }}
            </template>
          </el-table-column>
          <el-table-column label="CPU">
            <template #default="{ row }">
              {{ JSON.parse(row.computer_info).cpu }}
            </template>
          </el-table-column>
          <el-table-column label="内存">
            <template #default="{ row }">
              {{ (JSON.parse(row.computer_info).ram / (1024 * 1024 * 1024)).toFixed(2) }} GB
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="上传时间">
            <template #default="{ row }">
              {{ new Date(row.created_at).toLocaleString() }}
            </template>
          </el-table-column>
          <el-table-column label="操作">
            <template #default="{ row }">
              <el-button type="primary" link @click="viewDeviceDetail(row)">
                查看详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 设备详情对话框 -->
    <el-dialog
      v-model="deviceDetailVisible"
      title="设备详情"
      width="50%"
    >
      <div v-if="selectedDevice" class="device-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="用户名">
            {{ selectedDevice.name }}
          </el-descriptions-item>
          <el-descriptions-item label="上传时间">
            {{ new Date(selectedDevice.created_at).toLocaleString() }}
          </el-descriptions-item>
          <el-descriptions-item label="操作系统">
            {{ selectedDevice.computer_info.os }}
          </el-descriptions-item>
          <el-descriptions-item label="CPU">
            {{ selectedDevice.computer_info.cpu }}
          </el-descriptions-item>
          <el-descriptions-item label="内存">
            {{ (selectedDevice.computer_info.ram / (1024 * 1024 * 1024)).toFixed(2) }} GB
          </el-descriptions-item>
          <el-descriptions-item label="显卡">
            {{ selectedDevice.computer_info.gpu }}
          </el-descriptions-item>
          <el-descriptions-item label="影像文件" :span="2">
            {{ selectedDevice.image_file }}
          </el-descriptions-item>
          <el-descriptions-item label="影像描述" :span="2">
            {{ selectedDevice.image_description }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import ChartBox from '@/components/admin/ChartBox.vue'
import { User, Monitor, DataLine, Search } from '@element-plus/icons-vue'
import request from '@/utils/request'

// 日期范围选择
const dateRange = ref([])
const dateShortcuts = [
  {
    text: '最近一周',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
      return [start, end]
    }
  },
  {
    text: '最近一个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
      return [start, end]
    }
  },
  {
    text: '最近三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
      return [start, end]
    }
  }
]

// 统计数据
const stats = ref({
  totalUsers: 0,
  totalDevices: 0,
  uploadRate: 0
})

// 设备列表
const deviceList = ref([])
const searchQuery = ref('')
const searchType = ref('username')
const deviceDetailVisible = ref(false)
const selectedDevice = ref(null)

// 过滤后的设备列表
const filteredDeviceList = computed(() => {
  if (!searchQuery.value) return deviceList.value
  
  const query = searchQuery.value.toLowerCase()
  return deviceList.value.filter(device => {
    const computerInfo = JSON.parse(device.computer_info)
    switch (searchType.value) {
      case 'username':
        return device.user_name.toLowerCase().includes(query)
      case 'os':
        return computerInfo.os.toLowerCase().includes(query)
      case 'cpu':
        return computerInfo.cpu.toLowerCase().includes(query)
      case 'ram':
        return (computerInfo.ram / (1024 * 1024 * 1024)).toString().includes(query)
      default:
        return true
    }
  })
})

// 图表数据
const institutionChartData = ref({
  labels: [],
  datasets: [
    {
      label: '单位类型分布',
      data: [],
      backgroundColor: [],
      borderColor: [],
      borderWidth: 2
    }
  ]
})

const professionChartData = ref({
  labels: [],
  datasets: [
    {
      label: '学历分布',
      data: [],
      backgroundColor: [],
      borderColor: [],
      borderWidth: 2
    }
  ]
})

const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        padding: 20,
        usePointStyle: true,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const label = context.label || '';
          const value = context.parsed;
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    }
  }
}

// 获取统计数据
const fetchStats = async () => {
  try {
    const response = await request.get('/admin/stats/user-info', {
      params: {
        startDate: dateRange.value[0]?.toISOString().split('T')[0],
        endDate: dateRange.value[1]?.toISOString().split('T')[0]
      }
    })
    
    if (!response || !response.stats) {
      throw new Error('返回数据格式不正确')
    }
    
    // 直接使用后端返回的统计数据
    stats.value = {
      totalUsers: response.stats.totalUsers,
      totalDevices: response.stats.uploadCount,
      uploadRate: response.stats.uploadRate
    }
    
    // 更新单位类型分布图表
    updateInstitutionChart(response.institutionStats || [])
    
    // 更新学历分布图表
    updateProfessionChart(response.professionStats || [])
    
    // 更新设备信息列表
    deviceList.value = response.details || []
  } catch (error) {
    console.error('获取统计数据失败：', error)
    ElMessage.error(error.message || '获取统计数据失败')
  }
}

// 更新单位类型分布图表
const updateInstitutionChart = (institutionStats) => {
  if (!institutionStats || institutionStats.length === 0) {
    institutionChartData.value = {
      labels: [],
      datasets: [
        {
          label: '单位类型分布',
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 2
        }
      ]
    }
    return
  }

  // 生成动态颜色
  const colors = [
    'rgba(255, 99, 132, 0.8)',
    'rgba(54, 162, 235, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)',
    'rgba(199, 199, 199, 0.8)',
    'rgba(83, 102, 255, 0.8)',
    'rgba(255, 99, 132, 0.8)',
    'rgba(54, 162, 235, 0.8)'
  ]
  
  const borderColors = [
    'rgba(255, 99, 132, 1)',
    'rgba(54, 162, 235, 1)',
    'rgba(255, 206, 86, 1)',
    'rgba(75, 192, 192, 1)',
    'rgba(153, 102, 255, 1)',
    'rgba(255, 159, 64, 1)',
    'rgba(199, 199, 199, 1)',
    'rgba(83, 102, 255, 1)',
    'rgba(255, 99, 132, 1)',
    'rgba(54, 162, 235, 1)'
  ]

  institutionChartData.value = {
    labels: institutionStats.map(item => item.profession),
    datasets: [
      {
        label: '单位类型分布',
        data: institutionStats.map(item => item.count),
        backgroundColor: colors.slice(0, institutionStats.length),
        borderColor: borderColors.slice(0, institutionStats.length),
        borderWidth: 2
      }
    ]
  }
}

// 更新学历分布图表
const updateProfessionChart = (professionStats) => {
  if (!professionStats || professionStats.length === 0) {
    professionChartData.value = {
      labels: [],
      datasets: [
        {
          label: '学历分布',
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 2
        }
      ]
    }
    return
  }

  // 生成动态颜色
  const colors = [
    'rgba(255, 99, 132, 0.8)',
    'rgba(54, 162, 235, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)',
    'rgba(199, 199, 199, 0.8)',
    'rgba(83, 102, 255, 0.8)',
    'rgba(255, 99, 132, 0.8)',
    'rgba(54, 162, 235, 0.8)'
  ]
  
  const borderColors = [
    'rgba(255, 99, 132, 1)',
    'rgba(54, 162, 235, 1)',
    'rgba(255, 206, 86, 1)',
    'rgba(75, 192, 192, 1)',
    'rgba(153, 102, 255, 1)',
    'rgba(255, 159, 64, 1)',
    'rgba(199, 199, 199, 1)',
    'rgba(83, 102, 255, 1)',
    'rgba(255, 99, 132, 1)',
    'rgba(54, 162, 235, 1)'
  ]

  professionChartData.value = {
    labels: professionStats.map(item => item.organization),
    datasets: [
      {
        label: '学历分布',
        data: professionStats.map(item => item.count),
        backgroundColor: colors.slice(0, professionStats.length),
        borderColor: borderColors.slice(0, professionStats.length),
        borderWidth: 2
      }
    ]
  }
}

// 查看设备详情
const viewDeviceDetail = (device) => {
  selectedDevice.value = {
    ...device,
    computer_info: JSON.parse(device.computer_info)
  }
  deviceDetailVisible.value = true
}

// 搜索处理
const handleSearch = () => {
  if (!dateRange.value || dateRange.value.length !== 2) {
    ElMessage.warning('请选择日期范围')
    return
  }
  fetchStats()
}

// 清除搜索
const handleSearchClear = () => {
  searchQuery.value = ''
  searchType.value = 'username'
}

// 初始化
onMounted(() => {
  // 设置默认日期范围为最近一个月
  dateRange.value = dateShortcuts[1].value()
  // 初始化时加载数据
  fetchStats()
})
</script>

<style scoped>
.user-info-stats {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-content {
  text-align: center;
  padding: 20px 0;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.chart-container {
  margin-bottom: 20px;
}

.chart-content {
  height: 400px;
}

.device-list {
  margin-top: 20px;
}

.device-detail {
  padding: 20px;
}

.search-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-input {
  width: 240px;
}

.search-type {
  width: 120px;
}

.header-controls {
  display: flex;
  gap: 16px;
  align-items: center;
}

.date-search {
  display: flex;
  gap: 12px;
  align-items: center;
}
</style> 